import React from "react";
import { Link } from "react-router-dom";
import HeroSection from "../CommonComponents/HeroSection/HeroSection";
import VideoEmbed from "../CommonComponents/VideoEmbed/VideoEmbed";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import "./Home.css";
import AVConBanner from "./HomePageImages/AVConBanner.jpg";
import PiletPhoto from "./HomePageImages/_HAN7008.JPG";
import {
  ECOSYSTEM_DATA,
  HIGHLIGHTS,
  ACTION_LINKS,
  HOME_CONFIG,
  EXTERNAL_LINKS,
} from "../constants";
import AVConSchedule from "../EventSchedule/EventScheduleImages/AVConSchedule2026.jpeg";
import AvConSpeakerSchedule from "../EventSchedule/EventScheduleImages/Speakers Hub Schedule.png";

/**
 * EcosystemCard Component
 * Displays individual ecosystem items
 */
const EcosystemCard = ({ title, text }) => (
  <Box className="info-card">
    <Typography variant="h5">{title}</Typography>
    <p>{text}</p>
  </Box>
);

/**
 * HighlightItem Component
 * Displays individual highlight items
 */
const HighlightItem = ({ text }) => <Box className="highlight">{text}</Box>;

/**
 * Home Component
 * Main landing page with hero, ecosystem, and convention sections
 */
export default function Home() {
  return (
    <main className="home-page">
      {/* Hero Section */}
      <HeroSection
        defaultTitle={HOME_CONFIG.heroTitle}
        defaultSubtitle={HOME_CONFIG.heroSubtitle}
        smallScreenTitle={HOME_CONFIG.heroMobileTitle}
        smallScreenSubtitle={HOME_CONFIG.heroMobileSubtitle}
        buttonText={HOME_CONFIG.buttonText}
        buttonLink="/bookTickets"
      />

      {/* Introduction Section */}
      <section className="intro-section">
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <img src={PiletPhoto} alt="AvCon Aviation" className="home-image" />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h3">More Than an Event</Typography>
            <br />

            <Typography variant="h5">
              Building the Future of Aviation & Aerospace Together
            </Typography>
            <br />

            <p>
              AvCon is more than an annual event — it is a growing ecosystem
              connecting industry, education and government throughout the year.
            </p>

            <p>
              By creating opportunities for collaboration, career exploration
              and workforce development, AvCon helps inspire future talent while
              supporting the needs of the industry today.
            </p>
          </Grid>
        </Grid>
      </section>

      {/* Featured Live Stream */}
      <section
        className="live-stream-section"
        aria-labelledby="live-stream-title"
      >
        <div className="live-stream-heading">
          <p className="section-eyebrow">Live from AvCon 2026</p>

          <Typography variant="h2" component="h2" id="live-stream-title">
            Watch AvCon 2026 Live
          </Typography>
        </div>

        <div>
          <article className="stream-card stream-card--featured">
            <div className="stream-card__content">
              <p className="stream-card__description">
                Join the AvCon 2026 live stream on YouTube. Schools can use the
                education registration link below to submit questions during the
                broadcast.
              </p>
            </div>

            <VideoEmbed
              src="https://www.youtube.com/embed/R9WwaqI-8Lw"
              title="AvCon 2026 live stream"
            />

            <div className="stream-links">
              <a
                href="https://streamyard.com/watch/fXQZNAES3G5A"
                target="_blank"
                rel="noopener noreferrer"
                className="stream-link"
              >
                Schools: register to ask live questions
              </a>

              <a
                href="https://tyhub.ie/ty-podcast/"
                target="_blank"
                rel="noopener noreferrer"
                className="stream-link"
              >
                Also available on TY Hub
              </a>
            </div>
            <br/>
            <div className="video-grid">
              <img
                src={AVConSchedule}
                alt="AvCon 2026 event schedule"
                className="schedule-image"
              />
              <img
                src={AvConSpeakerSchedule}
                alt="AvCon 2026 speakers hub schedule"
                className="schedule-image"
              />
            </div>
          </article>
        </div>
      </section>

      {/* Ecosystem Section */}
      <section className="section">
        <Typography variant="h3" align="center">
          The AvCon Ecosystem
        </Typography>
        <br />
        <br />

        <Grid container spacing={3}>
          {ECOSYSTEM_DATA.map((item, index) => (
            <Grid item xs={12} md={4} key={index}>
              <EcosystemCard title={item.title} text={item.text} />
            </Grid>
          ))}
        </Grid>

        <div className="button-row">
          <Link to="/exhibitorRegistration" className="btn-link">
            Explore Industry Opportunities
          </Link>
        </div>
      </section>

      {/* Convention Section */}
      <section className="convention-section">
        <div className="banner-wrapper">
          <img
            src={AVConBanner}
            alt="AvCon 2026 Banner"
            className="banner-image"
          />

          <div className="banner-content">
            <Typography variant="h3">
              AvCon 2026 Student & Industry Convention
            </Typography>

            <Typography>Inspiring the Next Generation</Typography>
          </div>
        </div>

        <Grid container spacing={2} className="highlight-grid">
          {HIGHLIGHTS.map((item, index) => (
            <Grid item xs={6} md={4} key={index}>
              <HighlightItem text={item} />
            </Grid>
          ))}
        </Grid>

        <div className="button-row">
          <a href={EXTERNAL_LINKS.studentConventionForm} className="btn-link">
            Schools Pre-Register - AvCon 2027
          </a>

          <a
            href={EXTERNAL_LINKS.exhibitorRegistrationForm}
            className="btn-link"
          >
            Become a Partner
          </a>

          <a
            href={EXTERNAL_LINKS.avcon2027PreRegistrationForm}
            className="btn-link"
          >
            Pre-register for AvCon 2027
          </a>
        </div>
      </section>

      {/* Industry & CTA Section */}
      <section className="final-section">
        <hr />
        <br />
        <br />

        <Typography variant="h3">Why Organisations Get Involved</Typography>

        <p>
          The future of aviation depends on the people entering the industry
          today. AvCon gives organisations the opportunity to connect with
          future talent, build relationships with education and contribute to
          tomorrow's workforce.
        </p>

        <div className="button-row">
          {ACTION_LINKS.map((link) => (
            <Link key={link.label} to={link.to} className="btn-link">
              {link.label}
            </Link>
          ))}
        </div>

        <br />
        <hr />
        <br />
        <br />

        <Typography variant="h3">Be Part of the Future</Typography>

        <Typography>
          Connecting Industry. Inspiring Talent. Shaping Tomorrow's Workforce.
        </Typography>

        <div className="button-row">
          <Link to="/bookTickets" className="btn-link">
            Schools 2027 Pre-Register
          </Link>

          <Link to="/exhibitorRegistration" className="btn-link">
            Industry Opportunities
          </Link>

          <Link to="/contact" className="btn-link">
            Contact Us
          </Link>
        </div>
      </section>
    </main>
  );
}
